import { Grid, Paper } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router';
import { MainDashboardForm } from '.';
import * as api from '../../api/app/assetApi';
import * as commonApi from '../../api/app/commonApi';
import { ApiError } from '../../components/core/ApiError/ApiError';
import { Loader } from '../../components/core/Loader/Loader';
import { PageContent } from '../../components/core/PageContent/PageContent';
import { PageHeader } from '../../components/core/PageHeader/PageHeader';
import { useAuth } from '../../contexts/authWindows';
import { AssetMainDashboardSetting, MainDashboardInputs } from '../../types/app/asset.types';
import { UserRoles } from '../../types/core/auth.types';
import { dictionary } from '../../utils/core/dictionary';
import { convertDataFromInputToUpdateRequest } from '../../utils/core/functions/mainDashboard.functions';
import { useStyles } from './MainDashboardForm.styles';

export function MainDashboardDetail() {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const isReadOnly = user?.roles.includes(UserRoles.Admin) ? false : true;
  const { id } = useParams();
  const [defaultValues, setDefaultValues] = useState<AssetMainDashboardSetting | null>(null);
  const queryClient = useQueryClient();

  const { data: isUsedHydra } = useQuery<boolean>('useHydra', commonApi.useHydraDataService);
  const { mutate: mutateUpdateDashboard } = useMutation(api.updateMainDashBoardSettingForAsset);
  const { data, isLoading, isError } = useQuery<AssetMainDashboardSetting>(
    ['dashboardConfig'],
    () => api.getMainDashBoardSettingForAsset(Number(id)),
  );

  useEffect(() => {
    if (!data) return;

    setDefaultValues({
      id: data.id,
      timeAggregation: data.timeAggregation,
      qtyLevelGreen: data.qtyLevelGreen,
      qtyLevelOrange: data.qtyLevelOrange,
      showOrangeLevelLine: data.showOrangeLevelLine,
      taktTimeLineQty: data.taktTimeLineQty,
      showActualCycleTime: data.showActualCycleTime,
      showActualState: data.showActualState,
      showScrap: data.showScrap,
      showHydraMachineNumber: data.showHydraMachineNumber,
      showHydraOrderNumber: data.showHydraOrderNumber,
      showHydraToolNumber: data.showHydraToolNumber,
      showHydraPartNumber: data.showHydraPartNumber,
      showHydraStatus: data.showHydraStatus,
    });
  }, [data]);

  const handleOnSubmit: SubmitHandler<MainDashboardInputs> = (data: MainDashboardInputs) => {
    if (!defaultValues) return;
    if (!isUsedHydra) {
      data.showHydraMachineNumber = false;
      data.showHydraOrderNumber = false;
      data.showHydraToolNumber = false;
      data.showHydraPartNumber = false;
      data.showHydraStatus = false;
    }

    const requestData = convertDataFromInputToUpdateRequest({
      ...data,
      id: Number(id!),
    });

    mutateUpdateDashboard(
      { ...requestData, id: Number(id) },
      {
        onSuccess: () => {
          enqueueSnackbar(dictionary.DASHBOARD_EDITED, { variant: 'success' });
          queryClient.invalidateQueries(['dashboardConfig']);
        },
        onError: (result) => {
          enqueueSnackbar(`${result}`, { variant: 'error' });
        },
      },
    );
  };

  if (isError) return <ApiError />;
  if (isLoading || !defaultValues || isUsedHydra === undefined) return <Loader />;

  return (
    <>
      <PageHeader title={dictionary.DASHBOARD_EDIT} headlineVariant={'h5'} />
      <PageContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8} lg={4}>
            <Paper className={classes.paper}>
              <MainDashboardForm
                onSubmit={handleOnSubmit}
                defaultValues={defaultValues}
                isReadOnly={isReadOnly}
                isUsedHydra={isUsedHydra}
              />
            </Paper>
          </Grid>
        </Grid>
      </PageContent>
    </>
  );
}
